import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase/config";
import type {
  CreateUGCCharacterRequest,
  CreateUGCCharacterResponse,
  CreateUGCVideoJobRequest,
  CreateUGCVideoJobResponse,
  StartUGCVideoGenerationRequest,
  UGCVideoCharacter,
  UGCVideoJob,
} from "../types/ugcVideo";

const UGC_VIDEO_JOBS_COLLECTION = "ugcVideoJobs";
const UGC_VIDEO_CHARACTERS_COLLECTION = "ugcVideoCharacters";
const CREATE_UGC_VIDEO_ENDPOINT =
  process.env.REACT_APP_CREATE_UGC_VIDEO_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/createUGCVideo";
const START_UGC_VIDEO_GENERATION_ENDPOINT =
  process.env.REACT_APP_START_UGC_VIDEO_GENERATION_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/startUGCVideoGeneration";
const CREATE_UGC_CHARACTER_ENDPOINT =
  process.env.REACT_APP_CREATE_UGC_CHARACTER_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/createUGCVideoCharacter";

class UGCVideoService {
  private async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Authentication required. Please sign in.");
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData:
      | T
      | {
          error?: string;
          message?: string;
        }
      | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      throw new Error(
        detailedMessage || `Request failed with status ${response.status}`,
      );
    }

    return responseData as T;
  }

  private convertTimestampToISOString(value: any): string {
    if (value instanceof Timestamp) {
      return value.toDate().toISOString();
    }
    if (value?.toDate && typeof value.toDate === "function") {
      return value.toDate().toISOString();
    }
    const parsed = new Date(value || Date.now());
    if (Number.isNaN(parsed.getTime())) {
      return new Date().toISOString();
    }
    return parsed.toISOString();
  }

  private parseJob(id: string, data: Record<string, any>): UGCVideoJob {
    return {
      jobId: String(data.jobId || id),
      appId: String(data.appId || ""),
      appName: String(data.appName || ""),
      requesterUserId: String(data.requesterUserId || ""),
      status:
        data.status === "review_pending" ||
        data.status === "queued" ||
        data.status === "processing" ||
        data.status === "completed" ||
        data.status === "failed"
          ? data.status
          : "review_pending",
      phase:
        data.phase === "review" ||
        data.phase === "generation" ||
        data.phase === "completed" ||
        data.phase === "failed"
          ? data.phase
          : "review",
      inputs: {
        objective: String(data.inputs?.objective || ""),
        gender:
          data.inputs?.gender === "male" ||
          data.inputs?.gender === "female" ||
          data.inputs?.gender === "non_binary"
            ? data.inputs.gender
            : "female",
        hook: String(data.inputs?.hook || ""),
        lengthSeconds:
          Number.isFinite(Number(data.inputs?.lengthSeconds)) &&
          Number(data.inputs?.lengthSeconds) > 0
            ? Number(data.inputs.lengthSeconds)
            : 10,
        creativeDirection: String(data.inputs?.creativeDirection || ""),
        selectedCharacterId: data.inputs?.selectedCharacterId
          ? String(data.inputs.selectedCharacterId)
          : undefined,
        workflow: data.inputs?.workflow === "multi_frame" ? "multi_frame" : "direct",
        videoProvider:
          data.inputs?.videoProvider === "gemini" ? "gemini" : "kling",
        videoModel: String(
          data.inputs?.videoModel ||
            (data.inputs?.videoProvider === "gemini"
              ? "veo-3.1-fast-generate-preview"
              : "kling-v2.6-std"),
        ),
        aspectRatio:
          data.inputs?.aspectRatio === "16:9" || data.inputs?.aspectRatio === "1:1"
            ? data.inputs.aspectRatio
            : "9:16",
      },
      progress: {
        stage:
          data.progress?.stage === "review_pending" ||
          data.progress?.stage === "queued" ||
          data.progress?.stage === "planning" ||
          data.progress?.stage === "frame_generation" ||
          data.progress?.stage === "video_generation" ||
          data.progress?.stage === "persisting" ||
          data.progress?.stage === "completed" ||
          data.progress?.stage === "failed"
            ? data.progress.stage
            : "review_pending",
        message: String(data.progress?.message || ""),
        percent: Math.max(0, Math.min(100, Number(data.progress?.percent || 0))),
      },
      planning:
        data.planning && typeof data.planning === "object"
          ? data.planning
          : undefined,
      frameAssets:
        data.frameAssets && typeof data.frameAssets === "object"
          ? data.frameAssets
          : undefined,
      videoAsset:
        data.videoAsset && typeof data.videoAsset === "object"
          ? data.videoAsset
          : undefined,
      captionsByPlatform:
        data.captionsByPlatform && typeof data.captionsByPlatform === "object"
          ? data.captionsByPlatform
          : undefined,
      report:
        data.report && typeof data.report === "object" ? data.report : undefined,
      marketingAssetRecordId: data.marketingAssetRecordId
        ? String(data.marketingAssetRecordId)
        : undefined,
      error: data.error ? String(data.error) : undefined,
      createdAt: this.convertTimestampToISOString(data.createdAt),
      updatedAt: this.convertTimestampToISOString(data.updatedAt),
    };
  }

  private parseCharacter(id: string, data: Record<string, any>): UGCVideoCharacter {
    return {
      characterId: String(data.characterId || id),
      appId: String(data.appId || ""),
      appName: String(data.appName || ""),
      requesterUserId: String(data.requesterUserId || ""),
      status:
        data.status === "completed" ||
        data.status === "review_pending" ||
        data.status === "failed"
          ? data.status
          : "completed",
      includeAppContext: data.includeAppContext !== false,
      aiMode: data.aiMode !== false,
      inputs: {
        creativeDirection: String(data.inputs?.creativeDirection || ""),
        baseImagePrompt: String(data.inputs?.baseImagePrompt || ""),
        imageModel: String(data.inputs?.imageModel || "gpt-image-2.5-flare"),
      },
      characterProfile: {
        name: String(data.characterProfile?.name || "Alex"),
        ageRange: String(data.characterProfile?.ageRange || "25-34"),
        appearance: String(data.characterProfile?.appearance || ""),
        wardrobe: String(data.characterProfile?.wardrobe || ""),
        personality: String(data.characterProfile?.personality || ""),
        speakingStyle: String(data.characterProfile?.speakingStyle || ""),
      },
      characterPrompt: String(data.characterPrompt || ""),
      characterAsset:
        data.characterAsset && typeof data.characterAsset === "object"
          ? data.characterAsset
          : undefined,
      error: data.error ? String(data.error) : undefined,
      createdAt: this.convertTimestampToISOString(data.createdAt),
      updatedAt: this.convertTimestampToISOString(data.updatedAt),
    };
  }

  async createUGCVideoPlan(
    payload: CreateUGCVideoJobRequest,
  ): Promise<CreateUGCVideoJobResponse> {
    if (!payload.appId?.trim()) {
      throw new Error("Select an app.");
    }

    return this.postAuthenticatedJson<CreateUGCVideoJobResponse>(
      CREATE_UGC_VIDEO_ENDPOINT,
      payload as unknown as Record<string, unknown>,
    );
  }

  async startUGCVideoGeneration(
    payload: StartUGCVideoGenerationRequest,
  ): Promise<CreateUGCVideoJobResponse> {
    if (!payload.jobId?.trim()) {
      throw new Error("jobId is required.");
    }
    return this.postAuthenticatedJson<CreateUGCVideoJobResponse>(
      START_UGC_VIDEO_GENERATION_ENDPOINT,
      payload as unknown as Record<string, unknown>,
    );
  }

  async createUGCVideoCharacter(
    payload: CreateUGCCharacterRequest,
  ): Promise<CreateUGCCharacterResponse> {
    if (!payload.appId?.trim()) {
      throw new Error("Select an app.");
    }
    return this.postAuthenticatedJson<CreateUGCCharacterResponse>(
      CREATE_UGC_CHARACTER_ENDPOINT,
      payload as unknown as Record<string, unknown>,
    );
  }

  async listUGCVideoJobs(params?: { appId?: string }): Promise<UGCVideoJob[]> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    const requesterUserId = currentUser?.email || currentUser?.uid || "";
    if (!requesterUserId) {
      return [];
    }

    const constraints = [where("requesterUserId", "==", requesterUserId)];
    if (params?.appId) {
      constraints.push(where("appId", "==", params.appId));
    }

    const snapshot = await getDocs(
      query(collection(db, UGC_VIDEO_JOBS_COLLECTION), ...constraints),
    );

    return snapshot.docs
      .map((docSnap) => this.parseJob(docSnap.id, docSnap.data()))
      .sort(
        (left, right) =>
          new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
      );
  }

  async getUGCVideoJob(jobId: string): Promise<UGCVideoJob | null> {
    const snap = await getDoc(doc(db, UGC_VIDEO_JOBS_COLLECTION, jobId));
    if (!snap.exists()) {
      return null;
    }
    return this.parseJob(snap.id, snap.data());
  }

  async listUGCVideoCharacters(params?: { appId?: string }): Promise<UGCVideoCharacter[]> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    const requesterUserId = currentUser?.email || currentUser?.uid || "";
    if (!requesterUserId) {
      return [];
    }

    const constraints = [where("requesterUserId", "==", requesterUserId)];
    if (params?.appId) {
      constraints.push(where("appId", "==", params.appId));
    }

    const snapshot = await getDocs(
      query(collection(db, UGC_VIDEO_CHARACTERS_COLLECTION), ...constraints),
    );

    return snapshot.docs
      .map((docSnap) => this.parseCharacter(docSnap.id, docSnap.data()))
      .sort(
        (left, right) =>
          new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
      );
  }
}

const ugcVideoService = new UGCVideoService();
export default ugcVideoService;
